import 'uplot/dist/uPlot.min.css';

import {isEqual} from 'lodash';
import React, {
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type uPlot from 'uplot';
import UplotReact from 'uplot-react';

import type {HoveredIssueSource} from '../../../state/issues/hooks';
import {type IssueLine} from '../../../state/issues/types';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {type PlotFontSize} from '../../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../../util/plotHelpers/plotHelpers';
import {
  type LineType,
  type PlotType,
  type Timestep,
} from '../../../util/plotHelpers/types';
import {useLinePlotSettings} from '../../PanelRunsLinePlot/LinePlotSettingsContext';
import {
  type NumericLine,
  presentChartSeries,
  type RenderLine,
} from '../chartSeries';
import {useUpdateIconPositions} from '../LinePlotPlot';
import {type DomainMaybe} from '../types';
import {type IssueLinesConfig} from './drawIssueLines';
import {type NonFiniteMarkerState} from './drawNonFiniteMarkers';
import {type StackedAreaDrawState} from './drawStackedAreas';
import {getUPlotOptions} from './getUPlotOptions';
import {getuPlotMarginOverride} from './margins';
import {type PreparedUPlotData, prepareUPlotData} from './prepareUPlotData';
import {useReactVisUsageTracking} from './useReactVisUsageTracking';

const SUPPORTED_LINE_TYPES: Set<LineType> = new Set(['line', 'area']);

export function useShouldUseuPlot(
  lines: NumericLine[],
  plotType: PlotType | undefined,
  panelId: string
) {
  const {forceReactVis: forceReactVisSetting, uplotEnabled} =
    useLinePlotSettings();
  const firstUnsupportedLine = useMemo(
    () =>
      lines.find(
        line => line.kind == null || !SUPPORTED_LINE_TYPES.has(line.kind)
      ),
    [lines]
  );
  const areAllLinesuPlotSupported = firstUnsupportedLine == null;
  const uPlotUnsupportedLineType =
    firstUnsupportedLine == null
      ? undefined
      : (firstUnsupportedLine.kind ?? 'missing');
  const isChartTypeuPlotSupported =
    plotType === 'line' ||
    plotType === 'stacked-area' ||
    plotType === 'pct-area';
  const forceReactVis = forceReactVisSetting === true;
  const shouldUseuPlot =
    uplotEnabled &&
    areAllLinesuPlotSupported &&
    isChartTypeuPlotSupported &&
    !forceReactVis;
  useReactVisUsageTracking(
    shouldUseuPlot,
    {
      areAllLinesuPlotSupported,
      forceReactVis,
      isChartTypeuPlotSupported,
      plotType,
      uplotEnabled,
      uPlotUnsupportedLineType,
    },
    panelId
  );
  return shouldUseuPlot;
}

const emptyIssueLines: IssueLine[] = [];

/** Let marker drawing read the latest dimming and hover state without rebuilding the chart. */
function useNonFiniteMarkerState(
  dimFactor: number | undefined,
  uplotRef: React.RefObject<uPlot | null>
) {
  const stateRef = useRef<NonFiniteMarkerState>({
    hoveredMarkerIndex: -1,
    dimFactor,
  });
  const getState = useCallback(() => stateRef.current, []);
  useLayoutEffect(() => {
    if (stateRef.current.dimFactor === dimFactor) {
      return;
    }
    stateRef.current.dimFactor = dimFactor;
    uplotRef.current?.redraw(false);
  }, [dimFactor, uplotRef]);
  return getState;
}

/**
 * Let the stacked-area hook read the hovered runs without rebuilding the chart.
 * The highlight effect's `setSeries` calls trigger the redraw, and layout
 * effects run first, so that redraw sees the new runs.
 */
function useStackedAreaDrawState(
  highlightedSeriesIds: ReadonlySet<string> | undefined
) {
  const stateRef = useRef<StackedAreaDrawState>({highlightedSeriesIds});
  const getState = useCallback(() => stateRef.current, []);
  useLayoutEffect(() => {
    stateRef.current.highlightedSeriesIds = highlightedSeriesIds;
  }, [highlightedSeriesIds]);
  return getState;
}

function getNoHoveredIssue(): null {
  return null;
}

/** Redraws the chart when the hovered issue changes, without re-rendering. */
function useRedrawOnHoveredIssueChange(
  source: HoveredIssueSource | undefined,
  uplotRef: React.RefObject<uPlot | null>
) {
  useEffect(
    function subscribeToHoveredIssue() {
      if (source == null) {
        return undefined;
      }
      return source.subscribe(function redrawForHoveredIssue() {
        uplotRef.current?.redraw(false);
      });
    },
    [source, uplotRef]
  );
}

type LinePlotuPlotProps = {
  lines: RenderLine[];
  /** Stable panel ID, sent with telemetry. */
  panelId: string;
  /** Prepared line and band points shared by the main and highlight layers. Charts only read them. */
  preparedData?: PreparedUPlotData;
  width: number;
  height: number;
  xDomain: DomainMaybe;
  yDomain: DomainMaybe;
  fontSize: PlotFontSize;
  timestep: Timestep | undefined;
  /** Issue lines to render on the chart */
  issueLines?: IssueLine[];
  /**
   * Hovered issue for highlighting issue lines. Read when drawing, so a hover
   * change redraws the chart instead of rebuilding it. Must be stable.
   */
  hoveredIssueSource?: HoveredIssueSource;
  /** Whether the x-axis min is zoomed (if true, hide 'before' lines) */
  isZoomedXMin?: boolean;
  /** Whether the x-axis max is zoomed (if true, hide 'after' lines) */
  isZoomedXMax?: boolean;
  /** If true, this is the overlay layer that contains the highlighted lines */
  isOverlay?: boolean;
  /** If true, this is the base layer that draws the grid */
  isBaseLayer?: boolean;
  /**
   * Lines to draw at full brightness in the highlight layer.
   * Show or hide these lines on the existing chart so hovering does not rebuild it.
   */
  highlightedSeriesIds?: ReadonlySet<string>;
  dimFactor?: number;
  /** Use to override the default react-vis plot margin in the crosshair component */
  plotMarginOverride: PlotMargin | undefined;
  setPlotMarginOverride?: (plotMargin: PlotMargin) => void;
};

export const LinePlotuPlot: FC<LinePlotuPlotProps> = ({
  lines,
  panelId: panelID,
  preparedData,
  width,
  height,
  xDomain,
  yDomain,
  fontSize,
  timestep,
  issueLines = emptyIssueLines,
  hoveredIssueSource,
  isZoomedXMin = false,
  isZoomedXMax = false,
  isOverlay = false,
  isBaseLayer = false,
  highlightedSeriesIds,
  dimFactor,
  plotMarginOverride,
  setPlotMarginOverride,
}) => {
  const {isDarkMode, xAxisIsDatetime, xLogScale, yLogScale} =
    useLinePlotSettings();
  // update the issues line icon position to match the plot margin
  useUpdateIconPositions(xDomain, plotMarginOverride);

  const seriesEntriesResult = useMemo(
    function prepareLayerData() {
      return preparedData ?? prepareUPlotData(lines, xLogScale);
    },
    [preparedData, lines, xLogScale]
  );
  const {data} = seriesEntriesResult;
  const presentedEntries = useMemo(() => {
    const presentationById = new Map(lines.map(line => [line.seriesId, line]));
    return seriesEntriesResult.seriesEntries.map(entry => {
      if (entry == null) {
        return null;
      }
      // Missing presentation must not discard geometry or shift facet indices.
      const presentation =
        presentationById.get(entry.line.seriesId) ??
        presentChartSeries([entry.line], {}, {})[0];
      return {...entry, line: {...presentation, ...entry.line}};
    });
  }, [lines, seriesEntriesResult.seriesEntries]);
  const uplotRef = useRef<uPlot | null>(null);

  const getNonFiniteMarkerState = useNonFiniteMarkerState(dimFactor, uplotRef);
  const getStackedAreaDrawState = useStackedAreaDrawState(highlightedSeriesIds);

  // Only charts that draw issue lines read or follow the hovered issue.
  const drawnHoveredIssueSource =
    issueLines.length > 0 ? hoveredIssueSource : undefined;
  useRedrawOnHoveredIssueChange(drawnHoveredIssueSource, uplotRef);
  const getHoveredIssue = drawnHoveredIssueSource?.get ?? getNoHoveredIssue;

  const issueLinesConfig: IssueLinesConfig = useMemo(
    () => ({
      issueLines,
      getHoveredIssue,
      isZoomedXMin,
      isZoomedXMax,
    }),
    [issueLines, getHoveredIssue, isZoomedXMin, isZoomedXMax]
  );

  /**
   * Reuse the drawing settings until the data or chart settings change.
   * Marker, stacked-area, and issue-line drawing read changing hover and
   * dimming state through getters, so those changes do not rebuild the chart.
   */
  const baseOptions = getUPlotOptions(
    presentedEntries,
    seriesEntriesResult.seriesById,
    lines,
    getNonFiniteMarkerState,
    issueLinesConfig,
    xDomain,
    yDomain,
    xAxisIsDatetime,
    xLogScale,
    yLogScale,
    fontSize,
    timestep,
    isDarkMode,
    isOverlay,
    isBaseLayer,
    getStackedAreaDrawState
  );
  // Option identity matters here. uplot-react resizes the existing chart when
  // only width or height changes, but rebuilds it when any other option is a
  // new object. React Compiler caches baseOptions and options separately, so a
  // resize keeps baseOptions. LinePlotuPlot.resize.test.tsx fails if that
  // caching is lost.
  const options = {...baseOptions, width, height};
  const lastMarginRef = useRef<PlotMargin | null>(null);

  // Keep the crosshair aligned with the chart's actual drawing area after rendering.
  useEffect(() => {
    // Wait until the chart is ready; its drawing area is not accurate during setup.
    const u = uplotRef.current;
    if (setPlotMarginOverride == null || u == null || u.status !== 1) {
      return;
    }
    const margin = getuPlotMarginOverride(u.width, u.height, u.bbox);
    if (margin != null && !isEqual(margin, lastMarginRef.current)) {
      lastMarginRef.current = margin;
      setPlotMarginOverride(margin);
    }
    // Recheck after each render so resizing stays aligned; publish only changed margins.
  });

  // Show the selected lines in the highlight layer without rebuilding the chart.
  // Only change lines whose visibility differs, so moving along the same run
  // does not repeatedly update them.
  useEffect(() => {
    const u = uplotRef.current;
    if (u == null || highlightedSeriesIds == null) {
      return;
    }
    const {seriesEntries} = seriesEntriesResult;
    for (let i = 1; i < seriesEntries.length; i++) {
      const entry = seriesEntries[i];
      if (entry == null) {
        continue;
      }
      const shouldShow = highlightedSeriesIds.has(entry.line.seriesId);
      if (u.series[i]?.show !== shouldShow) {
        u.setSeries(i, {show: shouldShow});
      }
    }
  }, [highlightedSeriesIds, seriesEntriesResult]);

  const smoothedFromBackend = lines.some(l => l.smoothedFromBackend);

  const hasSmoothedLines = smoothedFromBackend || lines.some(l => l.smoothed);

  useEffect(() => {
    if (hasSmoothedLines) {
      recordRumAction('line_plot_smoothed', {
        panelID,
        smoothedFromBackend,
        uplot: true,
      });
    }
  }, [panelID, hasSmoothedLines, smoothedFromBackend]);

  return (
    <div
      className="w-full h-full relative"
      data-test="line-plot-uplot"
      data-smoothed-from-backend={smoothedFromBackend}>
      <UplotReact
        // Start a new chart when the number of lines/band edges
        // changes. The wrapper otherwise applies new settings to old data first,
        // which can fail because the chart expects a different data shape.
        key={seriesEntriesResult.seriesEntries.length}
        options={options}
        // uplot-react accepts facets at runtime, but declares only AlignedData.
        data={data as unknown as uPlot.AlignedData}
        onCreate={u => {
          uplotRef.current = u;
        }}
        onDelete={u => {
          if (uplotRef.current === u) {
            uplotRef.current = null;
          }
        }}
      />
      {/* Axis titles are rendered by the parent (LinePlot) in reserved strips
          outside the plot, so they no longer overlap the chart. */}
    </div>
  );
};
